import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-card border border-ink-100/80 bg-white/95 shadow-card backdrop-blur-sm',
        className,
      )}
      {...rest}
    />
  );
}

export function CardHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex items-start justify-between gap-4 px-5 py-4', className)} {...rest} />
  );
}

export function CardTitle({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-base font-semibold text-ink-900', className)} {...rest} />;
}

export function CardDescription({ className, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('mt-1 text-sm text-ink-500', className)} {...rest} />;
}

export function CardContent({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-5', className)} {...rest} />;
}

export function CardFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('border-t border-ink-100 px-5 py-4', className)} {...rest} />;
}

interface SectionCardProps {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Tarjeta con encabezado estándar (título + descripción opcional + acción).
 *
 * El encabezado lleva una banda con el degradado de marca y un filete vertical
 * a la izquierda del título: es lo que le da color a todas las pantallas del
 * portal sin tener que tocar cada página (antes eran tarjetas blancas sobre un
 * fondo blanco y el conjunto se veía apagado).
 *
 * Es una columna flexible: así el contenido (`CardContent`) ocupa el alto
 * disponible y los paneles de una misma fila del grid quedan simétricos.
 */
export function SectionCard({ title, description, action, children, className }: SectionCardProps) {
  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="border-b border-ink-100/70 bg-gradient-to-r from-brand-50/80 via-white to-white">
        <div className="flex min-w-0 items-start gap-2.5">
          <span
            aria-hidden="true"
            className="mt-0.5 h-5 w-1 shrink-0 rounded-full bg-gradient-to-b from-brand-500 to-accent-400"
          />
          <div className="min-w-0">
            <CardTitle>{title}</CardTitle>
            {description ? <CardDescription>{description}</CardDescription> : null}
          </div>
        </div>
        {action}
      </CardHeader>
      <CardContent className="min-h-0 flex-1">{children}</CardContent>
    </Card>
  );
}
